import React, { useState } from "react";
import { useFormik } from "formik";
import { object, string } from "yup";
import { Button, Language } from "../../../../../components";
import { TextField } from "../../../../../components/TextField/TextField";
import { handleReadySubmit } from "../../../../../utils";
import { useSettings } from "../../../../../context";

type RegistrationFormData = {
  organization: string;
  engName: string;
  title: string;
  telephoneNumber: string;
  email: string;
  do: string;
};

export const RegistrationForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { language, processText } = useSettings();
  const isEn = language === Language.EN;

  const infoDisplay = [
    {
      en_title: "Date",
      en_content: "23 May 2025 (Friday)",
      cn_title: "日期",
      cn_content: "2025年5月23日（星期五）",
    },
    {
      en_title: "Time",
      en_content: "3:00 – 4:00 pm",
      cn_title: "時間",
      cn_content: "下午3時至4時",
    },
    {
      en_title: "Language",
      en_content:
        "Cantonese, supplemented by presentation materials in Chinese or English",
      cn_title: "語言",
      cn_content: "粵語，並以中文或英文簡報輔助",
    },
    {
      en_title: "Format",
      en_content:
        "Webinar – Participants shall prepare necessary electronic device(s) (e.g. desktop PC or notebook) with Internet access to attend the webinar.",
      cn_title: "形式",
      cn_content:
        "網上會議，參加者需自備可連接上網的電子設備，如桌上電腦或手提電腦。",
    },
  ];

  const contentMsg = {
    en: {
      thxMsg: "Thank you for submitting your registration!",
    },
    cn: {
      thxMsg: "感謝您的登記！",
    },
  };

  const errorMessage = {
    en: {
      organization: "Please enter the Organisation / Company Name",
      engName: "Please enter the Full Name in English",
      title: "Please enter the Post Title",
      telephoneNumber: "Please enter the Tel. No.",
      email: "Please enter the Email Address",
    },
    cn: {
      organization: "請填寫機構/公司名稱",
      engName: "請填寫英文全名",
      title: "請填寫職位",
      telephoneNumber: "請填寫電話",
      email: "請填寫電郵",
    },
  };

  const content_text = isEn ? contentMsg.en : contentMsg.cn;
  const err_text = isEn ? errorMessage.en : errorMessage.cn;

  const registrationFormik = useFormik<RegistrationFormData>({
    initialValues: {
      organization: "",
      engName: "",
      title: "",
      telephoneNumber: "",
      email: "",
      do: "reg.info_session",
    },
    validationSchema: object({
      organization: string().required(processText(err_text.organization)),
      engName: string().required(processText(err_text.engName)),
      title: string().required(processText(err_text.title)),
      telephoneNumber: string().required(processText(err_text.telephoneNumber)),
      email: string().required(processText(err_text.email)),
    }),
    // TODO: will start once backend database is finished
    onSubmit: async (regData: RegistrationFormData) => {
      setLoading(true);

      try {
        const response = await fetch("../api/process.php", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(regData).toString(),
        });

        if (!response.ok) {
          throw new Error("Failed to send request");
        }

        const data = await response.json();

        if (data.result == "success") {
          alert(processText(content_text.thxMsg));
        } else {
          console.log(data);
        }
      } catch (err: any) {
        alert(err);
        console.error("Error:", err);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="w-full flex flex-col gap-[24px]">
      <div>
        <p className="text-heading-l mb-[1.5rem]" aria-level={1} role="heading">
          Testing and Certification Manpower Development Award Scheme 2025-26
          <br />
          {processText("2025-26年度檢測認證人力發展嘉許計劃")}
        </p>
        <p className="text-highlight-m" aria-level={2} role="heading">
          Information Session – Online Registration
          <br />
          {processText("網上簡介會 – 線上登記")}
        </p>
      </div>
      <div className="flex flex-col gap-[16px]">
        <dl>
          {infoDisplay.map((item, index) => {
            return (
              <div key={index} className="mt-[1rem]">
                <div className="flex flex-row gap-[5px]">
                  <dt className="text-highlight-m w-1/3 sm:w-1/6">
                    {item.en_title}
                  </dt>
                  <dd className="w-2/3 sm:w-5/6">{item.en_content}</dd>
                </div>
                <div className="flex flex-row gap-[5px]">
                  <dt className="text-highlight-m w-1/3 sm:w-1/6">
                    {processText(item.cn_title)}
                  </dt>
                  <dd className="w-2/3 sm:w-5/6">
                    {processText(item.cn_content)}
                  </dd>
                </div>
              </div>
            );
          })}
        </dl>
      </div>
      <hr />
      <div className="text-highlight-m mb-[1.5rem]">
        Please fill in the following form to register for the Information
        Session:
        <br />
        {processText("請填寫以下表格註冊，登記參加網上簡介會：")}
      </div>

      <div>
        <span className="italic">* Required Information</span>{" "}
        {processText("必填欄位")}
      </div>

      <form
        onSubmit={handleReadySubmit(registrationFormik)}
        noValidate
        className="flex flex-col gap-[24px]"
      >
        <div className="flex flex-col gap-[16px]">
          <div className="grid grid-cols-2 gap-[24px]">
            <div>
              <div className="text-highlight-m mb-[8px]">
                Organisation / Company Name {processText("機構/公司名稱")}*
              </div>
              <TextField
                name="organization"
                label={`Organisation / Company Name ${processText(
                  "機構/公司名稱"
                )}`}
                aria-label={`Organisation / Company Name ${processText(
                  "機構/公司名稱"
                )}`}
                required
                aria-required="true"
                id="organization-input"
                FormHelperTextProps={{
                  id: "organization-helper",
                }}
                inputProps={{
                  "aria-describedby": "organization-helper",
                  "aria-errormessage": registrationFormik.errors.organization
                    ? "organization-helper"
                    : undefined,
                  autoComplete: "organization",
                  "aria-label": "organization input",
                }}
                value={registrationFormik.values.organization}
                onChange={registrationFormik.handleChange}
                onBlur={registrationFormik.handleBlur}
                error={
                  registrationFormik.touched.organization &&
                  Boolean(registrationFormik.errors.organization)
                }
                helperText={
                  Boolean(registrationFormik.touched.organization) &&
                  registrationFormik.errors.organization
                }
                fullWidth
              />
            </div>

            <div>
              <div className="text-highlight-m mb-[8px]">
                Full Name in English {processText("英文全名")}*
              </div>
              <TextField
                name="engName"
                label={`Full Name in English ${processText("英文全名")}`}
                aria-label={`Full Name in English ${processText("英文全名")}`}
                required
                aria-required="true"
                id="engName-input"
                FormHelperTextProps={{
                  id: "engName-helper",
                }}
                inputProps={{
                  "aria-describedby": "engName-helper",
                  "aria-errormessage": registrationFormik.errors.engName
                    ? "engName-helper"
                    : undefined,
                  autoComplete: "engName",
                  "aria-label": "engName input",
                }}
                value={registrationFormik.values.engName}
                onChange={registrationFormik.handleChange}
                onBlur={registrationFormik.handleBlur}
                error={
                  registrationFormik.touched.engName &&
                  Boolean(registrationFormik.errors.engName)
                }
                helperText={
                  registrationFormik.touched.engName &&
                  registrationFormik.errors.engName
                }
                fullWidth
              />
            </div>

            <div>
              <div className="text-highlight-m mb-[8px]">
                Post Title {processText("職位")}*
              </div>
              <TextField
                name="title"
                label={`Post Title ${processText("職位")}`}
                aria-label={`Post Title ${processText("職位")}`}
                required
                aria-required="true"
                id="title-input"
                FormHelperTextProps={{
                  id: "title-helper",
                }}
                inputProps={{
                  "aria-describedby": "title-helper",
                  "aria-errormessage": registrationFormik.errors.title
                    ? "title-helper"
                    : undefined,
                  autoComplete: "title",
                  "aria-label": "title input",
                }}
                value={registrationFormik.values.title}
                onChange={registrationFormik.handleChange}
                onBlur={registrationFormik.handleBlur}
                error={
                  registrationFormik.touched.title &&
                  Boolean(registrationFormik.errors.title)
                }
                helperText={
                  registrationFormik.touched.title &&
                  registrationFormik.errors.title
                }
                fullWidth
              />
            </div>

            <div>
              <div className="text-highlight-m mb-[8px]">
                Tel. No. {processText("電話")}*
              </div>
              <TextField
                name="telephoneNumber"
                label={`Tel. No. ${processText("電話")}`}
                aria-label={`Tel. No. ${processText("電話")}`}
                required
                aria-required="true"
                id="telephoneNumber-input"
                FormHelperTextProps={{
                  id: "telephoneNumber-helper",
                }}
                inputProps={{
                  "aria-describedby": "telephoneNumber-helper",
                  "aria-errormessage": registrationFormik.errors.telephoneNumber
                    ? "telephoneNumber-helper"
                    : undefined,
                  autoComplete: "telephoneNumber",
                  "aria-label": "telephoneNumber input",
                }}
                value={registrationFormik.values.telephoneNumber}
                onChange={registrationFormik.handleChange}
                onBlur={registrationFormik.handleBlur}
                error={
                  registrationFormik.touched.telephoneNumber &&
                  Boolean(registrationFormik.errors.telephoneNumber)
                }
                helperText={
                  registrationFormik.touched.telephoneNumber &&
                  registrationFormik.errors.telephoneNumber
                }
                fullWidth
              />
            </div>

            <div>
              <div className="text-highlight-m mb-[8px]">
                Email Address {processText("電郵")}*
              </div>
              <TextField
                name="email"
                label={`Email Address ${processText("電郵")}`}
                aria-label={`Email Address ${processText("電郵")}`}
                required
                aria-required="true"
                id="email-input"
                FormHelperTextProps={{
                  id: "email-helper",
                }}
                inputProps={{
                  "aria-describedby": "email-helper",
                  "aria-errormessage": registrationFormik.errors.email
                    ? "email-helper"
                    : undefined,
                  autoComplete: "email",
                  "aria-label": "email input",
                }}
                value={registrationFormik.values.email}
                onChange={registrationFormik.handleChange}
                onBlur={registrationFormik.handleBlur}
                error={
                  registrationFormik.touched.email &&
                  Boolean(registrationFormik.errors.email)
                }
                helperText={
                  registrationFormik.touched.email &&
                  registrationFormik.errors.email
                }
                fullWidth
              />
            </div>
          </div>

          <div className="flex flex-col gap-[8px] w-full">
            <div>
              Successful enrollee(s) will be notified by email before the
              session.
              <br />
              {processText("成功登記的參加者將在簡介會前收到電郵通知。")}
            </div>
          </div>
        </div>

        <div className="w-full">
          <Button
            variant="contained"
            aria-label="submit"
            tabIndex={0}
            role="button"
            type="submit"
            sx={{
              textTransform: "none",
              width: "100%",
              backgroundColor: "#233f55",
              padding: "16px",
              fontSize: "16px",
              fontWeight: 600,
              lineHeight: "22px",
            }}
          >
            {language === Language.EN ? "Submit" : "提交"}
          </Button>
        </div>
      </form>
    </div>
  );
};
